import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash,
  UserCircle,
  Phone,
  Lock,
  Check,
  X,
  ArrowClockwise
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { clearAppCachesAndReload } from '../utils/forceUpdate';
import BlockedClientsSection from '../components/BlockedClientsSection';
import PendingManager from '../components/PendingManager';
import AppVersionCard from '../components/AppVersionCard';
import RevertRequestsSection from '../components/RevertRequestsSection';
import AdminProfileUpdate from '../components/AdminProfileUpdate';
import AppSettingsManagement from '../components/AppSettingsManagement';
import HandlerManagement from '../components/HandlerManagement';
import StatusManagement from '../components/StatusManagement';
import CustomAmountsSettings from '../components/CustomAmountsSettings';
import HeaderLabelsSettings from '../components/HeaderLabelsSettings';
import EditNotificationSettings from '../components/EditNotificationSettings';
import BackupRestore from '../components/BackupRestore';
import MaintenanceSettings from '../components/MaintenanceSettings';
import ChatAssignSettings from '../components/ChatAssignSettings';
import DisplayNamesSettings from '../components/DisplayNamesSettings';
import RegisteredDevicesSection from '../components/RegisteredDevicesSection';
import LoginAuditAndBlocklist from '../components/LoginAuditAndBlocklist';
import SupportBotOptionsSettings from '../components/SupportBotOptionsSettings';
import BotConfigEditor from '../components/BotConfigEditor';
import MBManagementSection from '../components/MBManagementSection';
import { PasswordResetRequests, FlaggedCustomers } from '../components/Phase3Sections';
import PermissionsHub from '../components/PermissionsHub';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import ClientsDirectorySection from '../components/ClientsDirectorySection';
import AppearanceSettings from '../components/AppearanceSettings';
import WithdrawReportModal from '../components/WithdrawReportModal';
import CallingAgentReportsSection from '../components/CallingAgentReportsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HANDLER_ROLES = [
  { value: 'deposit_handler', label: 'Deposit Handler', color: 'green' },
  { value: 'withdrawal_handler', label: 'Withdrawal Handler', color: 'blue' },
  { value: 'support_handler', label: 'Support Handler', color: 'purple' },
  { value: 'inquiry_handler', label: 'Inquiry Handler', color: 'orange' },
  { value: 'third_party_withdraw_handler', label: 'Third Party Withdraw Handler', color: 'red' },
  // Point 48 — stripped-down role: Client Directory home, call/WhatsApp
  // click logging, optional read-only chat view scoped to assigned MBs.
  { value: 'calling_handler', label: 'Calling Handler', color: 'teal' }
];

// Settings Grid Cards — each card either navigates to `route` OR opens sub-view via `key`
const SETTINGS_CARDS = [
  { key: 'pending-manager',   title: 'Pending Manager',   subtitle: 'See & clear every lit toggle', icon: '🧹', accent: '#334155' },
  { key: 'profile',           title: 'Update Profile',    subtitle: 'Mobile · Email · Password',     icon: '👤', accent: '#0284c7' },
  { key: 'analytics',         title: 'Analytics',         subtitle: 'Bot · Activity · IP · Blocked', icon: '📈', accent: '#0d9488' },
  { key: 'handlers',          title: 'Handlers',          subtitle: 'Create · Manage · MB assign',   icon: '🧑‍💼', accent: '#d97706' },
  { key: 'clients-directory', title: 'Clients Directory', subtitle: 'MB-wise · WhatsApp Broadcast',  icon: '📇', accent: '#0ea5e9' },
  { key: 'create-client',     title: 'Create Client',     subtitle: 'Add new customer account',      icon: '➕', accent: '#06b6d4', route: '/admin/create-client' },
  { key: '2fa',               title: '2FA Devices',       subtitle: 'Trusted devices',               icon: '🔐', accent: '#2563eb' },
  { key: 'app-settings-ring', opensSubView: 'app-settings', title: 'Ringtone & Sounds', subtitle: 'Message tone · Call ringtone',  icon: '🔔', accent: '#f59e0b' },
  { key: 'display-names',     title: 'Display Names',     subtitle: 'DWS branding',                  icon: '🎯', accent: '#0891b2' },
  { key: 'backup',            title: 'Backup & Restore',  subtitle: 'Data safety',                   icon: '💾', accent: '#059669' },
  { key: 'maintenance',       title: 'Maintenance Mode',  subtitle: 'Pause app for users',           icon: '🛠️', accent: '#d97706' },
  { key: 'chat-assign',       title: 'Chat Assign Settings', subtitle: 'One handler per N · Inquiry · Support-guest', icon: '🎯', accent: '#4f46e5' },
  { key: 'status',            title: 'Handler Status',    subtitle: 'Handler activity',              icon: '🟢', accent: '#16a34a' },
  { key: 'support-bot',       title: 'Support Bot',       subtitle: 'Quick-reply options',           icon: '💬', accent: '#7c3aed' },
  { key: 'bot-config',         title: 'Bot & Auto-Messages', subtitle: 'On/Off · Templates · Links',   icon: '🤖', accent: '#14b8a6' },
  { key: 'pwd-requests',       title: 'Password Requests', subtitle: 'Handler reset approvals',       icon: '🔑', accent: '#f43f5e' },
  { key: 'red-flag',           title: 'Suspicious Customers', subtitle: 'Red-flag list · action needed',    icon: '🚩', accent: '#dc2626' },
  { key: 'green-flag',         title: 'Premium Customers',    subtitle: 'Green-flag list · VIP',             icon: '🚩', accent: '#16a34a' },
  { key: 'delete-perm',       title: 'Permissions',       subtitle: 'Approval requests hub',          icon: '🔓', accent: '#dc2626' },
  { key: 'reports',           title: 'Deposit / Withdraw Reports', subtitle: 'Excel · PDF · MB-wise', icon: '📊', accent: '#2563eb' },
  { key: 'appearance',        title: 'Appearance',        subtitle: 'Theme · Font',                  icon: '🎨', accent: '#8b5cf6' },
  { key: 'links',             title: 'Manage Links',      subtitle: 'Customer-facing links',         icon: '🔗', accent: '#0d9488' },
  { key: 'mb',                title: 'MB Management',     subtitle: 'Create · Hide · Unhide MBs',    icon: '🏷️', accent: '#4f46e5' },
  { key: 'service-timelines', title: 'Service Timelines', subtitle: 'Deposit · Withdraw · Support requests', icon: '📊', accent: '#0891b2', route: '/admin/service-timelines' },
  { key: 'custom-amounts',    title: 'Custom Quick Chips', subtitle: 'Deposit · Withdraw · Support quick options', icon: '💵', accent: '#059669' },
  { key: 'header-labels',     title: 'Chat Header Labels', subtitle: 'Rename chat header buttons per chat type', icon: '🏷️', accent: '#db2777' },
  { key: 'edit-notification', title: 'Edit Entry Notification', subtitle: 'Customer ko edit hone par kya message jaye', icon: '✏️', accent: '#f59e0b' },
  // Point 49 — sent reports from the Calling Handler role's daily activity log.
  { key: 'calling-agent-reports', title: 'Calling Agent Reports', subtitle: 'Call/WhatsApp activity · sent reports', icon: '📞', accent: '#0d9488' },
];

const SUB_VIEW_LABELS = {
  'profile': 'Update Profile',
  'analytics': 'Analytics',
  'handlers': 'Handlers',
  'clients-directory': 'Clients Directory',
  '2fa': '2FA Devices',
  'app-settings': 'App Settings',
  'display-names': 'Display Names',
  'backup': 'Backup & Restore',
  'maintenance': 'Maintenance Mode',
  'chat-assign': 'Chat Assign Settings',
  'status': 'Handler Status',
  'custom-amounts': 'Custom Quick Chips',
  'header-labels': 'Chat Header Labels',
  'edit-notification': 'Edit Entry Notification',
  'support-bot': 'Support Bot',
  'bot-config': 'Bot & Auto-Messages',
  'pwd-requests': 'Password Requests',
  'red-flag': 'Suspicious Customers',
  'green-flag': 'Premium Customers',
  'delete-perm': 'Permissions',
  'reports': 'Deposit / Withdraw Reports',
  'appearance': 'Appearance',
  'links': 'Manage Links',
  'danger': 'Danger Zone',
  'blocked-clients': 'Blocked Clients',
  'mb': 'MB Management',
  'calling-agent-reports': 'Calling Agent Reports',
};

const AdminSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [handlers, setHandlers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsQuery, setSettingsQuery] = useState('');
  const settingsSearchRef = React.useRef(null);
  // Cmd+K / Ctrl+K keyboard shortcut to focus settings search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (settingsSearchRef.current) {
          settingsSearchRef.current.focus();
          settingsSearchRef.current.select?.();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingHandler, setEditingHandler] = useState(null);
  
  // Hard reset states
  const [showHardResetModal, setShowHardResetModal] = useState(false);
  const [showCleanSlateModal, setShowCleanSlateModal] = useState(false);
  // 3-step clean-slate state
  const [csStep, setCsStep] = useState(1); // 1=phone+pw · 2=await Google · 3=ack
  const [csPhone, setCsPhone] = useState('');
  const [csPassword, setCsPassword] = useState('');
  const [csBusy, setCsBusy] = useState(false);
  const [csAck, setCsAck] = useState('');
  const [csIntentToken, setCsIntentToken] = useState('');
  const [csResult, setCsResult] = useState(null);
  // Latest restorable snapshot (48h undo window)
  const [csSnapshot, setCsSnapshot] = useState(null);
  const [csUndoBusy, setCsUndoBusy] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [undoPassword, setUndoPassword] = useState('');
  // Migration Toolkit state
  const [migrateFile, setMigrateFile] = useState(null);
  const [migratePassword, setMigratePassword] = useState('');
  const [migrateBusy, setMigrateBusy] = useState(false);
  const [migrateProgress, setMigrateProgress] = useState({ step: '', pct: 0, log: [] });
  const [resetScope, setResetScope] = useState('admin');
  const [resetPassword, setResetPassword] = useState('');
  
  // Recycle bin states
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [showPendingManager, setShowPendingManager] = useState(false);
  const [deletedRooms, setDeletedRooms] = useState([]);
  const [selectedDeletedRoom, setSelectedDeletedRoom] = useState(null);
  
  // Links management states
  const [adminLinks, setAdminLinks] = useState([]);
  const [newLinkDesc, setNewLinkDesc] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [editingLink, setEditingLink] = useState(null);
  const [recycleBinLoading, setRecycleBinLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // For calendar filter
  const [filteredDeletedRooms, setFilteredDeletedRooms] = useState([]);
  
  // Recycle bin selection states
  const [recycleBinSelectionMode, setRecycleBinSelectionMode] = useState(false);
  const [selectedRecycleBinItems, setSelectedRecycleBinItems] = useState([]);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false); // admin, handler, customer, all
  // subView is synced with the URL so ESC / browser-back walks back one
  // section at a time (Settings → Handlers → ESC → Settings, not straight
  // to /admin-dashboard). Each `setSubView(key)` pushes a history entry.
  const [searchParams, setSearchParams] = useSearchParams();
  const subView = searchParams.get('section');
  const setSubView = (val) => {
    if (!val) {
      // Going back to the grid — replace so we don't double-stack history
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ section: val });
    }
  };
  const [topMenuOpen, setTopMenuOpen] = useState(false);

  // Point 1 — preserve the Settings GRID scroll on open→back. Save only while
  // the grid is visible (subView null) so sub-view scrolling never pollutes it;
  // restore whenever the grid becomes visible again.
  const gridScrollRef = useRef(null);
  useEffect(() => {
    if (subView) return;
    const el = gridScrollRef.current;
    if (!el) return;
    try {
      const saved = parseInt(sessionStorage.getItem('admin_settings_grid_scroll') || '0', 10);
      if (saved > 0) requestAnimationFrame(() => { if (gridScrollRef.current) gridScrollRef.current.scrollTop = saved; });
    } catch (_) {}
  }, [subView]);
  
  const [formData, setFormData] = useState({
    username: '',
    phone: '',
    password: '',
    role: 'deposit_handler'
  });

  useEffect(() => {
    loadHandlers();
    loadLinks();
  }, []);

  // Load latest restorable clean-slate snapshot whenever Danger Zone opens
  useEffect(() => {
    if (subView !== 'danger') return;
    const loadSnap = async () => {
      try {
        const res = await axios.get(`${API}/admin/clean-slate/latest-snapshot`);
        setCsSnapshot(res.data?.snapshot || null);
      } catch (_) { /* no snapshot */ }
    };
    loadSnap();

    // If we're returning from the Google callback with clean_slate=verified,
    // pick up the intent_token and jump the modal straight to step 3 (ack).
    if (searchParams.get('clean_slate') === 'verified') {
      const token = sessionStorage.getItem('clean_slate_intent_token');
      if (token) {
        setCsIntentToken(token);
        setCsStep(3);
        setShowCleanSlateModal(true);
      }
      // strip the query flag but keep section=danger
      setSearchParams({ section: 'danger' }, { replace: true });
    }
  }, [subView]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadHandlers = async () => {
    // Fix doc #4 — instant-cache: paint last-known handlers, then refresh.
    const _c = readInstantCache('admin_handlers', 'all');
    if (_c) { setHandlers(_c); setLoading(false); }
    try {
      const response = await axios.get(`${API}/users/handlers`);
      setHandlers(response.data);
      writeInstantCache('admin_handlers', 'all', response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load handlers:', error);
      toast.error('Failed to load handlers');
      setLoading(false);
    }
  };

  // Links CRUD
  const loadLinks = async () => {
    const _c = readInstantCache('admin_links', 'all');
    if (_c) setAdminLinks(_c);
    try {
      const response = await axios.get(`${API}/links`);
      setAdminLinks(response.data);
      writeInstantCache('admin_links', 'all', response.data);
    } catch (error) {
      console.error('Failed to load links:', error);
    }
  };

  const handleAddLink = async () => {
    if (!newLinkDesc.trim() || !newLinkUrl.trim()) {
      toast.error('Description and URL are required');
      return;
    }
    try {
      await axios.post(`${API}/links`, { description: newLinkDesc.trim(), url: newLinkUrl.trim() });
      toast.success('Link added!');
      setNewLinkDesc('');
      setNewLinkUrl('');
      loadLinks();
    } catch (error) {
      toast.error('Failed to add link');
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      await axios.delete(`${API}/links/${linkId}`);
      toast.success('Link deleted');
      loadLinks();
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const handleUpdateLink = async (linkId) => {
    if (!editingLink) return;
    try {
      await axios.put(`${API}/links/${linkId}`, {
        description: editingLink.description,
        url: editingLink.url
      });
      toast.success('Link updated');
      setEditingLink(null);
      loadLinks();
    } catch (error) {
      toast.error('Failed to update link');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.phone || !formData.password) {
      toast.error('All fields are required');
      return;
    }

    if (formData.phone.length !== 10) {
      toast.error('Phone must be 10 digits');
      return;
    }

    if (formData.password.length < 5) {
      toast.error('Password must be at least 5 characters');
      return;
    }

    try {
      await axios.post(`${API}/users/create-handler`, {
        username: formData.username,
        phone: formData.phone,
        password: formData.password,
        role: formData.role,
        chat_type: formData.role.replace('_handler', '')
      });
      
      toast.success('Handler created successfully!');
      setShowCreateModal(false);
      setFormData({ username: '', phone: '', password: '', role: 'deposit_handler' });
      loadHandlers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create handler';
      toast.error(msg);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.phone) {
      toast.error('Name and mobile number are required');
      return;
    }

    if (formData.phone.length !== 10) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    try {
      await axios.put(`${API}/users/handler/${editingHandler.id}`, {
        username: formData.username,
        phone: formData.phone,
        password: formData.password || undefined,
        role: formData.role,
        chat_type: formData.role.replace('_handler', '')
      });
      
      toast.success('Handler updated successfully!');
      setEditingHandler(null);
      setFormData({ username: '', phone: '', password: '', role: 'deposit_handler' });
      loadHandlers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update handler';
      toast.error(msg);
    }
  };

  const handleDelete = async (handlerId, handlerName) => {
    if (!window.confirm(`Are you sure you want to delete ${handlerName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/handler/${handlerId}`);
      toast.success('Handler deleted successfully!');
      loadHandlers();
    } catch (error) {
      toast.error('Failed to delete handler');
    }
  };

  const startEdit = (handler) => {
    setEditingHandler(handler);
    setFormData({
      username: handler.username,
      phone: handler.phone,
      password: '',
      role: handler.role
    });
  };

  const cancelEdit = () => {
    setEditingHandler(null);
    setFormData({ username: '', phone: '', password: '', role: 'deposit_handler' });
  };

  const getRoleColor = (role) => {
    const roleObj = HANDLER_ROLES.find(r => r.value === role);
    return roleObj?.color || 'gray';
  };

  const getRoleLabel = (role) => {
    const roleObj = HANDLER_ROLES.find(r => r.value === role);
    return roleObj?.label || role;
  };

  const handleHardReset = async () => {
    if (!resetPassword) {
      toast.error('Enter your admin password');
      return;
    }
    try {
      await axios.post(`${API}/admin/hard-reset-chats`, {
        scope: resetScope,
        admin_id: user.id,
        password: resetPassword
      });
      
      toast.success(`Chats reset successfully for ${resetScope === 'all' ? 'everyone' : resetScope}!`);
      setShowHardResetModal(false);
      setResetPassword('');
      
      if (resetScope === 'admin' || resetScope === 'all') {
        window.location.reload();
      }
    } catch (error) {
      console.error('Hard reset failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset chats');
    }
  };

  const loadRecycleBin = async () => {
    setRecycleBinLoading(true);
    try {
      const response = await axios.get(`${API}/admin/recycle-bin?admin_id=${user.id}`);
      setDeletedRooms(response.data);
      setFilteredDeletedRooms(response.data); // Initially show all
      setShowRecycleBin(true);
    } catch (error) {
      console.error('Failed to load recycle bin:', error);
      toast.error('Failed to load recycle bin');
    } finally {
      setRecycleBinLoading(false);
    }
  };
  
  // Filter deleted rooms by selected date
  useEffect(() => {
    if (!selectedDate) {
      setFilteredDeletedRooms(deletedRooms);
    } else {
      const filtered = deletedRooms.filter(item => {
        const deletedDate = new Date(item.deleted_at).toDateString();
        const selected = new Date(selectedDate).toDateString();
        return deletedDate === selected;
      });
      setFilteredDeletedRooms(filtered);
    }
  }, [selectedDate, deletedRooms]);

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleRecycleBinSelection = (itemId) => {
    if (selectedRecycleBinItems.includes(itemId)) {
      setSelectedRecycleBinItems(prev => prev.filter(id => id !== itemId));
    } else {
      setSelectedRecycleBinItems(prev => [...prev, itemId]);
    }
  };

  const selectAllRecycleBin = () => {
    if (selectedRecycleBinItems.length === deletedRooms.length) {
      setSelectedRecycleBinItems([]);
    } else {
      setSelectedRecycleBinItems(deletedRooms.map(item => item.id));
    }
  };

  const handleDeleteFromRecycleBin = async () => {
    try {
      if (selectedRecycleBinItems.length === 1) {
        await axios.delete(`${API}/admin/recycle-bin/item/${selectedRecycleBinItems[0]}`);
      } else {
        await axios.post(`${API}/admin/recycle-bin/bulk-delete`, {
          item_ids: selectedRecycleBinItems
        });
      }
      
      // Remove from local state
      setDeletedRooms(prev => prev.filter(item => !selectedRecycleBinItems.includes(item.id)));
      setSelectedRecycleBinItems([]);
      setRecycleBinSelectionMode(false);
      toast.success('Items deleted from recycle bin!');
      
    } catch (error) {
      console.error('Failed to delete from recycle bin:', error);
      toast.error('Failed to delete items');
    }
  };

  const handleClearAllRecycleBin = async () => {
    try {
      await axios.post(`${API}/admin/recycle-bin/clear-all`);
      
      setDeletedRooms([]);
      setShowClearAllConfirm(false);
      toast.success('Recycle bin cleared!');
      
    } catch (error) {
      console.error('Failed to clear recycle bin:', error);
      toast.error('Failed to clear recycle bin');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <div className="bg-[#25D366] text-white p-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (!subView) { navigate('/admin-dashboard'); return; }
              // Fix doc #2 — let an open drill-down section step back one internal
              // level first; only exit to the Settings grid if none consumed it.
              const ev = new CustomEvent('dws:settings-back', { cancelable: true });
              window.dispatchEvent(ev);
              if (ev.defaultPrevented) return;
              navigate(-1);
            }}
            className="p-2 hover:bg-green-600 rounded-full"
            data-testid="admin-settings-back-btn"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">{subView ? SUB_VIEW_LABELS[subView] || 'Settings' : 'Settings'}</h1>
            <p className="text-sm text-green-100">{subView ? 'Admin Control Center' : 'Choose a section'}</p>
          </div>
          {!subView && (
            <button
              onClick={() => clearAppCachesAndReload()}
              className="mr-1 px-3 py-1.5 text-xs font-semibold bg-white/20 hover:bg-white/30 text-white rounded flex items-center gap-1"
              data-testid="admin-force-update-btn"
              title="Load the latest app version (no logout)"
            >
              <ArrowClockwise size={14} weight="bold" /> Force Update
            </button>
          )}
          {/* 3-dot menu — houses Danger Zone (hidden from casual view) */}
          {!subView && (
            <div className="relative" data-testid="settings-top-menu">
              <button
                onClick={() => setTopMenuOpen((v) => !v)}
                className="text-white/90 hover:text-white p-2"
                data-testid="settings-top-menu-btn"
                title="More options"
              >
                <span className="text-xl leading-none">⋮</span>
              </button>
              {topMenuOpen && (
                <div className="absolute right-0 top-10 bg-white text-slate-800 rounded-lg shadow-xl border border-slate-200 min-w-[180px] py-1 z-40">
                  <button
                    onClick={() => { setTopMenuOpen(false); setSubView('danger'); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 text-red-700 font-semibold flex items-center gap-2"
                    data-testid="settings-top-menu-danger"
                  >
                    <span>⚠️</span> Danger Zone
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        ref={gridScrollRef}
        onScroll={() => { try { if (!subView && gridScrollRef.current) sessionStorage.setItem('admin_settings_grid_scroll', String(gridScrollRef.current.scrollTop)); } catch (_) {} }}
        className="flex-1 overflow-y-auto p-4"
        style={{ paddingBottom: '100px' }}
      >
        <div className="max-w-4xl mx-auto">
          
          {/* GRID VIEW — shown only when no subView is active */}
          {!subView && (
            <>
              {/* Professional Settings Hub — Quick Navigation Grid */}
              <div className="mb-6" data-testid="settings-quick-nav">
                <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Admin Control Center</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Choose a section to open. Each card opens on its own page.</p>
                  </div>
                  <div className="relative flex-shrink-0 w-full sm:w-72">
                    <span className="absolute left-3 top-3 text-slate-400 text-sm">🔍</span>
                    <input
                      ref={settingsSearchRef}
                      type="text"
                      value={settingsQuery}
                      onChange={(e) => setSettingsQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { setSettingsQuery(''); e.currentTarget.blur(); } }}
                      placeholder="Search settings…  (⌘K / Ctrl+K)"
                      data-testid="settings-search-input"
                      className="w-full pl-9 pr-14 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
                    />
                    <span className="hidden sm:flex absolute right-2 top-2 items-center gap-0.5 text-[10px] font-semibold text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 bg-slate-50 pointer-events-none select-none">
                      ⌘K
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-3">
                  {SETTINGS_CARDS
                    .filter(c => !settingsQuery.trim() ||
                      c.title.toLowerCase().includes(settingsQuery.toLowerCase()) ||
                      c.subtitle.toLowerCase().includes(settingsQuery.toLowerCase()))
                    .map(c => (
                    <button
                      key={c.key}
                      onClick={() => {
                        if (c.key === 'pending-manager') { setShowPendingManager(true); return; }
                        if (c.route) navigate(c.route);
                        else setSubView(c.opensSubView || c.key);
                      }}
                      data-testid={`quicknav-${c.key}`}
                      className="text-left rounded-xl border border-slate-200 hover:border-blue-400 hover:shadow-lg bg-white p-4 flex flex-col gap-3 transition-all group min-h-[130px]"
                    >
                      <div className="flex items-center justify-between">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ background: `${c.accent}15`, color: c.accent }}>
                          {c.icon}
                        </div>
                        <div className="text-slate-300 group-hover:text-blue-500 text-lg">›</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 text-[15px] leading-tight">{c.title}</div>
                        <div className="text-[12px] text-slate-500 mt-1 leading-snug">{c.subtitle}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="max-w-md">
                <AppVersionCard />
              </div>
            </>
          )}

          {/* SUB-VIEW: PROFILE */}
          {subView === 'profile' && (
            <AdminProfileUpdate 
              adminId={user.id} 
              currentPhone={user.phone}
              currentEmail={user.email || ''}
            />
          )}

          {/* SUB-VIEW: HANDLERS */}
          {subView === 'handlers' && (
            <div className="space-y-4">
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-green-600 transition-colors"
                data-testid="handlers-create-new-btn"
              >
                <Plus size={24} weight="bold" />
                Create New Handler
              </button>
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b">
                  <h2 className="font-semibold text-gray-800">All Handlers ({handlers.length})</h2>
                </div>
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : handlers.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <UserCircle size={64} weight="thin" className="mx-auto mb-2" />
                    <p>No handlers yet. Create one!</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {handlers.map((handler) => (
                      <div key={handler.id} className="p-4">
                        {editingHandler?.id === handler.id ? (
                          <form onSubmit={handleEdit} className="space-y-3">
                            <div>
                              <label className="text-sm font-medium text-gray-700">Name</label>
                              <input
                                type="text"
                                value={formData.username}
                                onChange={(e) => setFormData({...formData, username: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Mobile Number (Login ID)</label>
                              <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, '');
                                  if (val.length <= 10) setFormData({...formData, phone: val});
                                }}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                                placeholder="10-digit mobile number"
                                maxLength={10}
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">New Password (optional)</label>
                              <input
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({...formData, password: e.target.value})}
                                placeholder="Leave empty to keep current"
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Role</label>
                              <select
                                value={formData.role}
                                onChange={(e) => setFormData({...formData, role: e.target.value})}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                              >
                                {HANDLER_ROLES.map(role => (
                                  <option key={role.value} value={role.value}>{role.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <button type="submit" className="flex-1 bg-green-500 text-white py-2 rounded-lg font-semibold hover:bg-green-600 flex items-center justify-center gap-2">
                                <Check size={20} weight="bold" /> Save
                              </button>
                              <button type="button" onClick={cancelEdit} className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-300 flex items-center justify-center gap-2">
                                <X size={20} weight="bold" /> Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-800">{handler.username}</h3>
                              <p className="text-sm text-gray-500">{handler.phone}</p>
                              <span className={`inline-block mt-1 text-xs px-2 py-1 rounded-full bg-${getRoleColor(handler.role)}-100 text-${getRoleColor(handler.role)}-700`}>
                                {getRoleLabel(handler.role)}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => startEdit(handler)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                <Pencil size={20} weight="bold" />
                              </button>
                              <button onClick={() => handleDelete(handler.id, handler.username)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                                <Trash size={20} weight="bold" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <HandlerManagement />
            </div>
          )}

          {subView === '2fa' && (
            <div className="space-y-4">
              <RegisteredDevicesSection />
              <LoginAuditAndBlocklist />
            </div>
          )}
          {subView === 'app-settings' && (<AppSettingsManagement />)}
          {subView === 'display-names' && (<DisplayNamesSettings userId={user.id} />)}
          {subView === 'backup' && (<BackupRestore adminId={user.id} />)}
          {subView === 'maintenance' && (<MaintenanceSettings />)}
          {subView === 'chat-assign' && (<ChatAssignSettings adminId={user.id} />)}
          {subView === 'status' && (<StatusManagement user={user} />)}
          {subView === 'custom-amounts' && (<CustomAmountsSettings adminId={user.id} />)}
          {subView === 'header-labels' && (<HeaderLabelsSettings adminId={user.id} />)}
          {subView === 'edit-notification' && (<EditNotificationSettings adminId={user.id} />)}
          {subView === 'support-bot' && (<SupportBotOptionsSettings actorId={user?.id} />)}
          {subView === 'bot-config' && (<BotConfigEditor adminId={user?.id} />)}
          {subView === 'pwd-requests' && (<PasswordResetRequests adminId={user?.id} />)}
          {subView === 'red-flag' && (<FlaggedCustomers flag="red" />)}
          {subView === 'green-flag' && (<FlaggedCustomers flag="green" />)}
          {subView === 'delete-perm' && (<PermissionsHub adminId={user?.id} />)}
          {subView === 'reports' && (
            <WithdrawReportModal
              open={true}
              onClose={() => navigate(-1)}
              actorRole="super_admin"
            />
          )}
          {subView === 'clients-directory' && (<ClientsDirectorySection actorRole="super_admin" />)}
          {subView === 'calling-agent-reports' && (<CallingAgentReportsSection adminId={user.id} />)}
          {subView === 'appearance' && (<AppearanceSettings />)}
          {subView === 'blocked-clients' && (<BlockedClientsSection userId={user.id} userRole={user.role} />)}
          {subView === 'revert-requests' && (<RevertRequestsSection userId={user.id} />)}
          {subView === 'mb' && (<MBManagementSection user={user} />)}

          {/* SUB-VIEW: ANALYTICS HUB */}
          {subView === 'analytics' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { title: 'Support Analytics', subtitle: 'Bot sessions & handler performance', icon: '🤖', accent: '#0d9488', route: '/admin/support-analytics' },
                { title: 'Client Activity Dashboard', subtitle: 'Real-time activity tracking', icon: '📊', accent: '#3b82f6', route: '/admin-activity' },
                { title: 'Inactive Clients Tracker', subtitle: 'PDF export · Re-engagement', icon: '⏰', accent: '#ef4444', route: '/inactive-clients' },
                { title: 'Clients with Same IP', subtitle: 'Duplicate detection', icon: '🌐', accent: '#f97316', route: '/admin/clients-by-ip' },
                { title: 'Blocked Clients', subtitle: 'Blocked users list', icon: '🚫', accent: '#dc2626', onClick: () => setSubView('blocked-clients') },
                { title: 'Revert Requests', subtitle: 'Handler-submitted revert approvals', icon: '↩️', accent: '#2563eb', onClick: () => setSubView('revert-requests') },
                { title: 'MB Setup Configuration', subtitle: 'Labels + Auto-route', icon: '🏷️', accent: '#7c3aed', route: '/admin/mb-config' },
              ].map(a => (
                <button
                  key={a.title}
                  onClick={() => { if (a.route) navigate(a.route); else if (a.onClick) a.onClick(); }}
                  data-testid={`analytics-card-${a.title.toLowerCase().replace(/\s+/g,'-')}`}
                  className="text-left rounded-xl border border-slate-200 hover:border-blue-400 hover:shadow-md bg-white p-4 flex items-start gap-3 transition-all group"
                >
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 text-xl" style={{ background: `${a.accent}15`, color: a.accent }}>
                    {a.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">{a.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{a.subtitle}</div>
                  </div>
                  <div className="text-slate-400 group-hover:text-blue-500">›</div>
                </button>
              ))}
            </div>
          )}

          {/* SUB-VIEW: LINKS */}
          {subView === 'links' && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-xl">🔗</span> Manage Links
              </h3>
              <p className="text-sm text-gray-500 mb-4">Add links visible to all customers in their "Links" tab</p>
              {adminLinks.map((link) => (
                <div key={link.id} className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
                  {editingLink?.id === link.id ? (
                    <div className="space-y-2">
                      <input type="text" value={editingLink.description} onChange={(e) => setEditingLink({...editingLink, description: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Description" />
                      <input type="text" value={editingLink.url} onChange={(e) => setEditingLink({...editingLink, url: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="URL" />
                      <div className="flex gap-2">
                        <button onClick={() => handleUpdateLink(link.id)} className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"><Check size={16} /></button>
                        <button onClick={() => setEditingLink(null)} className="px-3 py-1 bg-gray-400 text-white rounded text-sm hover:bg-gray-500"><X size={16} /></button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 text-sm">{link.description}</p>
                        <p className="text-xs text-green-600 truncate mt-0.5">{link.url}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0 ml-2">
                        <button onClick={() => setEditingLink({...link})} className="p-1.5 hover:bg-gray-200 rounded transition-colors"><Pencil size={16} className="text-blue-500" /></button>
                        <button onClick={() => handleDeleteLink(link.id)} className="p-1.5 hover:bg-gray-200 rounded transition-colors"><Trash size={16} className="text-red-500" /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {adminLinks.length === 0 && <p className="text-center text-gray-400 text-sm py-3">No links added yet</p>}
              <div className="mt-4 space-y-2 border-t pt-4">
                <input type="text" value={newLinkDesc} onChange={(e) => setNewLinkDesc(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Link description (e.g., Telegram Group)" />
                <input type="text" value={newLinkUrl} onChange={(e) => setNewLinkUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="URL (e.g., https://t.me/group)" />
                <button onClick={handleAddLink} className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-sm flex items-center justify-center gap-2">
                  <Plus size={18} /> Add Link
                </button>
              </div>
            </div>
          )}

          {/* SUB-VIEW: DANGER ZONE */}
          {subView === 'danger' && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">Hard Reset Chats</h3>
                <p className="text-sm text-red-700 mb-4">Permanently delete chat rooms. This action cannot be undone!</p>
                <button onClick={() => setShowHardResetModal(true)} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold" data-testid="danger-hard-reset-btn">
                  🗑️ Hard Reset Chats
                </button>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Recycle Bin</h3>
                <p className="text-sm text-blue-700 mb-4">View deleted chats from last 60 days.</p>
                <button onClick={loadRecycleBin} disabled={recycleBinLoading} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg font-semibold" data-testid="danger-recycle-bin-btn">
                  {recycleBinLoading ? 'Loading...' : '🗑️ Open Recycle Bin'}
                </button>
              </div>
              <div className="bg-purple-50 border border-purple-300 rounded-lg p-4">
                <h3 className="font-semibold text-purple-900 mb-2">🚀 Production Clean-Slate</h3>
                <p className="text-sm text-purple-800 mb-1"><b>One-click reset for a freshly deployed production instance.</b></p>
                <ul className="text-xs text-purple-700 list-disc pl-5 mb-3 space-y-0.5">
                  <li>Keeps: super admin, all handlers, bot config, MB config, app settings</li>
                  <li>Wipes: every customer, chat, message, group, deposit/withdraw entry, saved photo/text/sticker, approvals, sessions, guest data</li>
                  <li>Also empties GCS <code>chat_media/</code> folder</li>
                  <li><b>3-step gate:</b> phone + password → Google verification (dwsglobal07@gmail.com) → typed acknowledgement</li>
                  <li><b>48-hour undo window</b> — a full snapshot is taken before the wipe</li>
                </ul>
                <button
                  onClick={() => { setCsStep(1); setCsPhone(''); setCsPassword(''); setCsAck(''); setCsIntentToken(''); setCsResult(null); setShowCleanSlateModal(true); }}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold"
                  data-testid="danger-clean-slate-btn"
                >🚀 Production Clean-Slate</button>
              </div>

              {csSnapshot && (
                <div className="bg-emerald-50 border-2 border-emerald-400 rounded-lg p-4" data-testid="clean-slate-undo-card">
                  <h3 className="font-semibold text-emerald-900 mb-1">♻️ Undo Clean-Slate available</h3>
                  <p className="text-sm text-emerald-800 mb-2">
                    A snapshot taken by <b>{csSnapshot.actor_username || 'admin'}</b> at{' '}
                    {new Date(csSnapshot.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} is still restorable.
                  </p>
                  <div className="text-xs text-emerald-700 mb-3">
                    <b>{csSnapshot.total_docs}</b> total docs · <b>{csSnapshot.hours_left}h</b> left in the 48-hour window
                    (expires {new Date(csSnapshot.expires_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })})
                  </div>
                  <button
                    onClick={() => { setUndoPassword(''); setShowUndoModal(true); }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg font-semibold text-sm"
                    data-testid="clean-slate-undo-btn"
                  >♻️ Restore Snapshot</button>
                </div>
              )}

              {/* Migration Toolkit */}
              <div className="bg-indigo-50 border border-indigo-300 rounded-lg p-4" data-testid="migration-card">
                <h3 className="font-semibold text-indigo-900 mb-2">📥 Migration Toolkit — Import from OLD app</h3>
                <p className="text-sm text-indigo-800 mb-2">
                  Purani Emergent app se export ki hui JSON file yahaan upload karo. Sabhi customers automatically <b>MB9</b> me tag ho jaayenge. Admin + current handler untouched rahenge.
                </p>
                <ol className="text-xs text-indigo-700 list-decimal pl-5 mb-3 space-y-0.5">
                  <li>Purani app pe: <code>GET /api/admin/migrate/export?actor_id=&lt;id&gt;&password=&lt;pw&gt;</code> → JSON download</li>
                  <li>Yahaan (naya app): niche upload karo</li>
                  <li>Domain switch: Emergent → Deployment → Custom Domain me apna purana domain daalo</li>
                </ol>
                <input
                  type="file" accept=".json,application/json"
                  onChange={(e) => setMigrateFile(e.target.files?.[0] || null)}
                  className="w-full mb-2 text-xs"
                  data-testid="migrate-file-input"
                />
                <input
                  type="password" placeholder="Admin password"
                  value={migratePassword} onChange={(e) => setMigratePassword(e.target.value)}
                  className="w-full px-2 py-1.5 border border-indigo-200 rounded text-sm mb-2"
                  data-testid="migrate-password-input"
                />
                <button
                  onClick={async () => {
                    if (!migrateFile) { toast.error('Please choose the export JSON file'); return; }
                    if (!migratePassword) { toast.error('Password required'); return; }
                    setMigrateBusy(true);
                    setMigrateProgress({ step: 'Reading file…', pct: 0, log: [] });
                    try {
                      const text = await migrateFile.text();
                      const bundle = JSON.parse(text);
                      const CHUNK = 500;
                      const totalItems = (() => {
                        let t = (bundle.customers || []).length;
                        t += ((bundle.collections?.users) || []).length;
                        for (const k of Object.keys(bundle.collections || {})) {
                          if (k !== 'users') t += (bundle.collections[k] || []).length;
                        }
                        return t || 1;
                      })();
                      let done = 0;
                      const log = [];
                      const postChunk = async (chunk_type, docs) => {
                        if (!docs || docs.length === 0) return;
                        for (let i = 0; i < docs.length; i += CHUNK) {
                          const slice = docs.slice(i, i + CHUNK);
                          const res = await axios.post(`${API}/admin/migrate/import-chunk`, {
                            actor_id: user?.id, password: migratePassword,
                            target_mb: 'MB9', chunk_type, docs: slice,
                          }, { timeout: 60000 });
                          done += slice.length;
                          const pct = Math.min(99, Math.round((done / totalItems) * 100));
                          setMigrateProgress(p => ({
                            step: `${chunk_type}: +${res.data?.inserted || 0} inserted, ${res.data?.skipped || 0} skipped`,
                            pct,
                            log: [...p.log, `${chunk_type} chunk ${Math.floor(i / CHUNK) + 1}: +${res.data?.inserted || 0}/${slice.length}`],
                          }));
                        }
                      };

                      // 1) users (legacy 'customers' + new 'collections.users')
                      const allUsers = [
                        ...(bundle.customers || []),
                        ...((bundle.collections?.users) || []),
                      ];
                      await postChunk('users', allUsers);

                      // 2) every whitelisted collection
                      const ALLOWED = ['chat_rooms', 'messages', 'group_messages', 'groups',
                        'deposit_entries', 'withdraw_entries', 'client_bank_details',
                        'saved_photos', 'saved_texts', 'guest_support', 'inquiries', 'call_history'];
                      for (const coll of ALLOWED) {
                        await postChunk(coll, (bundle.collections || {})[coll] || []);
                      }

                      // 3) links (legacy `app_settings_slice.links` + real `collections.links`)
                      const links = [
                        ...((bundle.app_settings_slice || {}).links || []),
                        ...((bundle.collections || {}).links || []),
                      ];
                      if (links.length) await postChunk('links', links);

                      // 4) app_settings global doc
                      const globalDoc = ((bundle.collections || {}).app_settings || []).find(s => s?.type === 'global');
                      const legacySlice = bundle.app_settings_slice;
                      const finalGlobal = globalDoc || (legacySlice ? {
                        support_phone: legacySlice.support_phone || legacySlice.support_number,
                        whatsapp_link: null,
                      } : null);
                      if (finalGlobal) await postChunk('app_settings_global', [finalGlobal]);

                      setMigrateProgress({ step: 'Complete', pct: 100, log });
                      toast.success(`Migration complete — ${done} docs processed.`);
                      setMigrateFile(null); setMigratePassword('');
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || e?.message || 'Import failed');
                    } finally { setMigrateBusy(false); }
                  }}
                  disabled={migrateBusy || !migrateFile || !migratePassword}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-semibold text-sm"
                  data-testid="migrate-import-btn"
                >{migrateBusy ? 'Importing…' : '📥 Import & Tag as MB9'}</button>

                {migrateBusy && (
                  <div className="mt-3 bg-white/60 border border-indigo-200 rounded p-2" data-testid="migrate-progress">
                    <div className="flex justify-between text-[11px] text-indigo-800 mb-1">
                      <span>{migrateProgress.step}</span><span>{migrateProgress.pct}%</span>
                    </div>
                    <div className="h-1.5 bg-indigo-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-600 transition-all" style={{ width: `${migrateProgress.pct}%` }}></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Production Clean-Slate Modal — 3-step gate */}
      {showCleanSlateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="clean-slate-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-semibold text-purple-700">🚀 Production Clean-Slate</h3>
              <div className="flex gap-1">
                {[1, 2, 3].map(n => (
                  <span
                    key={n}
                    className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center ${
                      csStep === n ? 'bg-purple-600 text-white' : csStep > n ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}
                  >{csStep > n ? '✓' : n}</span>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Step {csStep} of 3 · {csStep === 1 ? 'Phone + Password' : csStep === 2 ? 'Google Verification' : 'Type-to-confirm'}
            </p>

            {csResult ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-3">
                <div className="font-bold text-emerald-800 mb-1">✅ Clean-Slate Complete</div>
                <div className="text-xs text-emerald-700 space-y-0.5">
                  <div>Snapshot: <code className="font-mono">{csResult.snapshot_id?.slice(0, 8)}…</code></div>
                  <div>Undo window: <b>{csResult.undo_window_hours}h</b></div>
                  <div>Wiped: customers <b>{csResult.stats?.customers || 0}</b>, rooms <b>{csResult.stats?.chat_rooms || 0}</b>, messages <b>{csResult.stats?.messages || 0}</b>, groups <b>{csResult.stats?.groups || 0}</b></div>
                </div>
              </div>
            ) : csStep === 1 ? (
              <>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Admin Phone (username)</label>
                <input
                  type="text" value={csPhone} onChange={(e) => setCsPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 focus:outline-none focus:border-purple-500"
                  placeholder="e.g. 1558089654" data-testid="clean-slate-phone-input"
                />
                <label className="block text-sm font-semibold text-gray-700 mb-1">Admin Password</label>
                <input
                  type="password" value={csPassword} onChange={(e) => setCsPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:outline-none focus:border-purple-500"
                  placeholder="Password" data-testid="clean-slate-password-input"
                />
              </>
            ) : csStep === 2 ? (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3 text-sm text-purple-800">
                Redirecting to Google… If nothing happens, click the button below.
              </div>
            ) : (
              <>
                <div className="bg-yellow-50 border border-yellow-300 rounded p-2 text-xs text-yellow-800 mb-3">
                  ✓ Phone + password verified. ✓ Google verified. This is the final step — a 48-hour restorable snapshot will be taken automatically.
                </div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Type <b>CLEAN-SLATE</b> to confirm</label>
                <input
                  type="text" value={csAck} onChange={(e) => setCsAck(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:outline-none focus:border-purple-500"
                  placeholder="CLEAN-SLATE" data-testid="clean-slate-ack-input"
                />
              </>
            )}

            <div className="flex gap-2 mt-3">
              <button
                onClick={() => {
                  setShowCleanSlateModal(false);
                  sessionStorage.removeItem('clean_slate_intent_token');
                  sessionStorage.removeItem('clean_slate_google_verified');
                }}
                disabled={csBusy}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50"
                data-testid="clean-slate-cancel-btn"
              >{csResult ? 'Close' : 'Cancel'}</button>
              {!csResult && csStep === 1 && (
                <button
                  onClick={async () => {
                    if (!csPhone || !csPassword) { toast.error('Phone + password required'); return; }
                    setCsBusy(true);
                    try {
                      const res = await axios.post(`${API}/admin/clean-slate/init`, { phone: csPhone, password: csPassword });
                      const token = res.data?.intent_token;
                      if (!token) throw new Error('No intent_token');
                      sessionStorage.setItem('clean_slate_intent_token', token);
                      setCsIntentToken(token);
                      setCsStep(2);
                      // Redirect to Emergent Google auth
                      const redirectUrl = window.location.origin + '/admin-clean-slate-callback';
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Verification failed');
                    } finally { setCsBusy(false); }
                  }}
                  disabled={csBusy || !csPhone || !csPassword}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg font-semibold"
                  data-testid="clean-slate-next-btn"
                >{csBusy ? 'Verifying…' : 'Next → Google'}</button>
              )}
              {!csResult && csStep === 2 && (
                <button
                  onClick={() => {
                    const redirectUrl = window.location.origin + '/admin-clean-slate-callback';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold"
                  data-testid="clean-slate-retry-google-btn"
                >Open Google Verify</button>
              )}
              {!csResult && csStep === 3 && (
                <button
                  onClick={async () => {
                    if (csAck !== 'CLEAN-SLATE') { toast.error('Please type CLEAN-SLATE exactly'); return; }
                    setCsBusy(true);
                    try {
                      const res = await axios.post(`${API}/admin/clean-slate/confirm`, {
                        intent_token: csIntentToken, acknowledgement: csAck,
                      });
                      setCsResult(res.data);
                      toast.success(`Clean-slate done. Snapshot ${res.data?.snapshot_id?.slice(0, 8)}… kept for 48h.`);
                      sessionStorage.removeItem('clean_slate_intent_token');
                      sessionStorage.removeItem('clean_slate_google_verified');
                      // Refresh snapshot state so undo card shows up
                      try {
                        const s = await axios.get(`${API}/admin/clean-slate/latest-snapshot`);
                        setCsSnapshot(s.data?.snapshot || null);
                      } catch (_) {}
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Clean-slate failed');
                    } finally { setCsBusy(false); }
                  }}
                  disabled={csBusy || csAck !== 'CLEAN-SLATE'}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg font-semibold"
                  data-testid="clean-slate-confirm-btn"
                >{csBusy ? 'Wiping…' : 'Wipe Everything'}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Undo Clean-Slate Modal */}
      {showUndoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="undo-clean-slate-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold text-emerald-700 mb-1">♻️ Restore Snapshot</h3>
            <p className="text-sm text-gray-600 mb-3">
              This will restore every wiped customer, chat, group and entry from the snapshot back into the DB.
              Documents that already exist in production (same <code>id</code>) are skipped so live production data is untouched.
            </p>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Admin Password</label>
            <input
              type="password" value={undoPassword} onChange={(e) => setUndoPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:border-emerald-500"
              placeholder="Enter your password" data-testid="clean-slate-undo-password-input"
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setShowUndoModal(false); setUndoPassword(''); }}
                disabled={csUndoBusy}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50"
                data-testid="clean-slate-undo-cancel-btn"
              >Cancel</button>
              <button
                onClick={async () => {
                  if (!undoPassword) { toast.error('Password required'); return; }
                  setCsUndoBusy(true);
                  try {
                    const res = await axios.post(`${API}/admin/clean-slate/undo`, {
                      actor_id: user?.id, password: undoPassword,
                      snapshot_id: csSnapshot?.id,
                    });
                    const rs = res.data?.restore_stats || {};
                    const totalColl = Object.values(rs.collections || {}).reduce((a, b) => a + b, 0);
                    toast.success(`Restored ${rs.users_restored || 0} users + ${totalColl} docs from snapshot.`);
                    setShowUndoModal(false);
                    setUndoPassword('');
                    setCsSnapshot(null); // hide card
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Restore failed');
                  } finally { setCsUndoBusy(false); }
                }}
                disabled={csUndoBusy || !undoPassword}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg font-semibold"
                data-testid="clean-slate-undo-confirm-btn"
              >{csUndoBusy ? 'Restoring…' : 'Yes, Restore'}</button>
            </div>
          </div>
        </div>
      )}



      {/* Hard Reset Modal */}
      {showHardResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold text-red-600 mb-4">⚠️ Hard Reset Chats</h3>
            <p className="text-gray-600 mb-4">
              Select the scope of deletion:
            </p>

            <div className="space-y-3 mb-6">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="resetScope"
                  value="admin"
                  checked={resetScope === 'admin'}
                  onChange={(e) => setResetScope(e.target.value)}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-semibold">Admin Panel Only</div>
                  <div className="text-sm text-gray-500">Remove chats from your dashboard only</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="resetScope"
                  value="handler"
                  checked={resetScope === 'handler'}
                  onChange={(e) => setResetScope(e.target.value)}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-semibold">All Handlers</div>
                  <div className="text-sm text-gray-500">Remove from all handler panels</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="resetScope"
                  value="customer"
                  checked={resetScope === 'customer'}
                  onChange={(e) => setResetScope(e.target.value)}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-semibold">All Customers</div>
                  <div className="text-sm text-gray-500">Remove from all customer chats</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-red-300 rounded-lg cursor-pointer hover:bg-red-50">
                <input
                  type="radio"
                  name="resetScope"
                  value="all"
                  checked={resetScope === 'all'}
                  onChange={(e) => setResetScope(e.target.value)}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-semibold text-red-600">Delete Everything</div>
                  <div className="text-sm text-red-500">Permanently delete all chats and messages from server</div>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowHardResetModal(false);
                  setResetScope('admin');
                  setResetPassword('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <div className="flex-1 flex flex-col gap-2">
                <input
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full px-3 py-2 border rounded-lg outline-none focus:border-red-500"
                  data-testid="hard-reset-password-input"
                />
                <button
                  onClick={handleHardReset}
                  disabled={!resetPassword}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50"
                  data-testid="confirm-hard-reset-btn"
                >
                  Confirm Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Create New Handler</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <UserCircle size={16} weight="bold" className="inline mr-1" />
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  placeholder="Enter handler name"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone size={16} weight="bold" className="inline mr-1" />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/\D/g, '')})}
                  placeholder="10 digit mobile number"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  maxLength="10"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Lock size={16} weight="bold" className="inline mr-1" />
                  Password *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Minimum 5 characters"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Handler Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  required
                >
                  {HANDLER_ROLES.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600"
                >
                  Create Handler
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ username: '', phone: '', password: '', role: 'deposit_handler' });
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Recycle Bin Modal */}
      {showRecycleBin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">🗑️ Recycle Bin ({filteredDeletedRooms.length} of {deletedRooms.length} items)</h3>
              <button
                onClick={() => {
                  setShowRecycleBin(false);
                  setSelectedDeletedRoom(null);
                  setRecycleBinSelectionMode(false);
                  setSelectedRecycleBinItems([]);
                  setSelectedDate(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Calendar Filter */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <label className="text-sm font-semibold text-gray-700">📅 Filter by Date:</label>
                <input
                  type="date"
                  value={selectedDate || ''}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  min={new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate(null)}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Showing deletions from last 6 months {selectedDate && `(Filtered: ${new Date(selectedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })})`}
              </p>
            </div>

            {/* Action Buttons */}
            {deletedRooms.length > 0 && (
              <div className="flex gap-2 mb-4">
                {!recycleBinSelectionMode ? (
                  <>
                    <button
                      onClick={() => setRecycleBinSelectionMode(true)}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                      Select Items
                    </button>
                    <button
                      onClick={() => setShowClearAllConfirm(true)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      Clear All
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={selectAllRecycleBin}
                      className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                      {selectedRecycleBinItems.length === deletedRooms.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <button
                      onClick={handleDeleteFromRecycleBin}
                      disabled={selectedRecycleBinItems.length === 0}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Delete Selected ({selectedRecycleBinItems.length})
                    </button>
                    <button
                      onClick={() => {
                        setRecycleBinSelectionMode(false);
                        setSelectedRecycleBinItems([]);
                      }}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            )}

            {filteredDeletedRooms.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>{selectedDate ? 'No chats deleted on this date' : 'No deleted chats in recycle bin'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredDeletedRooms.map((item) => (
                  <div
                    key={item.id}
                    className={`border rounded-lg p-4 hover:bg-gray-50 cursor-pointer ${
                      selectedRecycleBinItems.includes(item.id) ? 'bg-blue-50 border-blue-500' : ''
                    }`}
                    onClick={() => {
                      if (recycleBinSelectionMode) {
                        toggleRecycleBinSelection(item.id);
                      } else {
                        setSelectedDeletedRoom(item);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {recycleBinSelectionMode && (
                          <input
                            type="checkbox"
                            checked={selectedRecycleBinItems.includes(item.id)}
                            onChange={() => toggleRecycleBinSelection(item.id)}
                            className="mt-1"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">
                              {item.customer_data?.username || 'Unknown Customer'}
                            </h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              item.room_data?.chat_type === 'deposit'
                                ? 'bg-green-100 text-green-700'
                                : item.room_data?.chat_type === 'withdrawal'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {item.room_data?.chat_type || 'N/A'}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              item.deletion_type === 'bulk'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {item.deletion_type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            Phone: {item.customer_data?.phone || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Deleted: {formatDateTime(item.deleted_at)}
                          </p>
                          <p className="text-xs text-gray-500">
                            Messages: {item.last_messages?.length || 0} (last 10 shown)
                          </p>
                        </div>
                      </div>
                      {!recycleBinSelectionMode && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDeletedRoom(item);
                          }}
                          className="text-blue-600 hover:text-blue-700 text-sm"
                        >
                          View Details →
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Clear All Confirmation */}
      {showClearAllConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-red-600 mb-2">⚠️ Clear All?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to permanently delete all {deletedRooms.length} items from recycle bin? 
              This action cannot be undone!
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowClearAllConfirm(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleClearAllRecycleBin}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deleted Room Detail Modal */}
      {selectedDeletedRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Chat Details</h3>
              <button
                onClick={() => setSelectedDeletedRoom(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Customer Information</h4>
                <p><strong>Name:</strong> {selectedDeletedRoom.customer_data?.username}</p>
                <p><strong>Phone:</strong> {selectedDeletedRoom.customer_data?.phone}</p>
                <p><strong>Role:</strong> {selectedDeletedRoom.customer_data?.role}</p>
              </div>

              {/* Deletion Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Deletion Information</h4>
                <p><strong>Deleted At:</strong> {formatDateTime(selectedDeletedRoom.deleted_at)}</p>
                <p><strong>Deletion Type:</strong> {selectedDeletedRoom.deletion_type}</p>
                <p><strong>Chat Type:</strong> {selectedDeletedRoom.room_data?.chat_type}</p>
              </div>

              {/* Messages */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold mb-2">
                  Last Messages ({selectedDeletedRoom.last_messages?.length || 0})
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedDeletedRoom.last_messages?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded ${
                        msg.sender_id === selectedDeletedRoom.customer_data?.id
                          ? 'bg-white'
                          : 'bg-green-100'
                      }`}
                    >
                      <p className="text-sm">{msg.text}</p>
                      {msg.image_url && <p className="text-xs text-gray-500">📷 Image attached</p>}
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDateTime(msg.timestamp)}
                      </p>
                    </div>
                  )) || <p className="text-gray-500">No messages</p>}
                </div>
              </div>
            </div>

            <button
              onClick={() => setSelectedDeletedRoom(null)}
              className="w-full mt-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {showPendingManager && (
        <PendingManager user={user} onClose={() => setShowPendingManager(false)} />
      )}
    </div>
  );
};

export default AdminSettings;
